'use client';
import React, { forwardRef } from 'react';
import { Input } from 'antd';
import Label from './Label';
import HelperText from './HelperText';
import cn from 'classnames';

type Props = {
  id?: string;
  label?: string | React.ReactNode;
  maxLength?: number;
  required?: boolean;
  optional?: boolean;
  helperText?: string;
  status?: 'error' | 'success';
  disabled?: boolean;
  showMaxLength?: boolean;
  value?: string;
  labelHelperText?: string;
  labelClassName?: string;
} & Omit<React.ComponentProps<typeof Input.TextArea>, 'status' | 'onFocus'>;

const TextArea = forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      id,
      maxLength,
      required,
      optional,
      helperText,
      status,
      labelHelperText,
      labelClassName,
      disabled,
      showMaxLength = true,
      value,
      className,
      showCount,
      ...props
    }: Props,
    ref
  ) => {
    return (
      <div className="!">
        {label && (
          <Label
            labelHelperText={labelHelperText}
            className={labelClassName}
            required={required}
            optional={optional}
            text={label}
            htmlFor={id}
          />
        )}
        <Input.TextArea
          id={id}
          value={value}
          disabled={disabled}
          ref={ref}
          style={{ resize: 'none' }}
          className={cn(
            '!h-[156px] !rounded-[12px] !p-[20px] !bg-[#FFFFFF] dark:!bg-dark-surface-primary !border-[1px] !border-[#E7E7E9] dark:!border-dark-input-border !resize-none [&_textarea::placeholder]:!text-[#ACACAC] placeholder:!text-base [&_textarea::placeholder]:!text-base placeholder:!font-normal [&_textarea::placeholder]:!font-normal placeholder:!leading-6 [&_textarea::placeholder]:!leading-6 placeholder:!tracking-normal [&_textarea::placeholder]:!tracking-normal !max-h-full hover:!border-pink-500 hover:dark:!border-pink-500 hover:!border-2 focus:!border-pink-500 focus:!border-3 dark:text-white ' +
            className,
            {
              '!border-red-500 !border-[1.5px]': status === 'error',
              '!border-green-500 !border-[1.5px]': status === 'success',
              '[&_textarea]:dark:border [&_textarea]:dark:!border-dark-text-placeholder [&_textarea]:!bg-light-button-label-primary [&_textarea]:dark:!bg-transparent':
                disabled,
            }
          )}
          maxLength={maxLength}
          showCount={showCount || showMaxLength ? { formatter: ({ count, maxLength: max }) => <div className="!text-[#090909] text-xs !font-normal mt-2">{`${count}${max ? `/${max}` : ''}`}</div> } : false}
          {...props}
        />
        <div
          className={cn('flex items-center justify-between mt-2', {
            'flex-row-reverse': !helperText,
          })}
        >
          {helperText && (
            <HelperText text={helperText} disabled={disabled} status={status} />
          )}
        </div>
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
